import React, { useState, useEffect } from 'react';
import { Rnd } from 'react-rnd';
import { Box, Tooltip } from '@mui/material';
import { ButtonRender } from './ButtonRender';
import { useStateBasedProps } from './useStateBasedProps';
import { DraggableResizableButtonProps } from './DraggableResizeButton';
import '../ToolsBar.css';
import { AcceptRejectDialog } from '../../CommonFunctions/DialogComponents';
import { acceptBtnStyle, rejectBtnStyle } from '../ButtonDialogStyles';

const DraggableResizableButton: React.FC<DraggableResizableButtonProps> = ({
  id,
  x,
  y,
  width,
  height,
  icon,
  selected,
  onClick,
  onChange,
  hasUrl,
  onDelete,
  backgroundColor = undefined,
  backgroundAlpha = 100,
  borderColor = '#2bb9bb',
  borderWidth = 2,
  borderRadius = 12,
  iconScale = 1,
  iconColor = '#2bb9bb',
  dashMode = 'Edit',
  contentOrder = ['icon'],
  text,
  iconStyle,
  imageUrl,
  containerProps,
  iconProps,
  textProps,
  imageProps,
  tooltip,
  hoverEnabled = true,
  softUI = false,
  colorScheme = 'peacock',
  dashboardHeight,
  dashboardWidth,
  gridSize,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const isPresentationMode = dashMode === 'Present' || dashMode === 'TV Display';

  // Default tooltips for edit and presentation modes
  const DEFAULT_EDIT_TOOLTIP = 'Press CTRL + click to follow link';
  const DEFAULT_PRESENT_TOOLTIP = 'Press click to follow link';

  // tooltip text logic so that default present mode tooltip is used until default tooltip text change
  let tooltipText;
  if (isPresentationMode) {
    tooltipText =
      !tooltip?.text || tooltip.text === DEFAULT_EDIT_TOOLTIP
        ? DEFAULT_PRESENT_TOOLTIP
        : tooltip.text;
  } else {
    tooltipText = tooltip?.text || DEFAULT_EDIT_TOOLTIP;
  }

  const currentBackgroundColor = useStateBasedProps(backgroundColor, isHovered, hoverEnabled);
  const currentBorderColor = useStateBasedProps(borderColor, isHovered, hoverEnabled);
  const currentBorderWidth = useStateBasedProps(borderWidth, isHovered, hoverEnabled);
  const currentBorderRadius = useStateBasedProps(borderRadius, isHovered, hoverEnabled);
  const currentContainerProps = useStateBasedProps(containerProps, isHovered, hoverEnabled) || {};
  const currentRotation = currentContainerProps.rotation || 0;
  const currentBackgroundAlpha = useStateBasedProps(backgroundAlpha, isHovered, hoverEnabled);

  const handleMouseEnter = () => {
    if (hoverEnabled) setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (hoverEnabled) setIsHovered(false);
  };

  const getBoxShadow = () => {
    if (!softUI) return 'none';
    switch (colorScheme) {
      case 'dark':
        return '0 0 20px rgba(0,0,0,0.6)';
      case 'chalk':
        return '0 0 18px rgba(0,0,0,0.4)';
      case 'halloween':
        return '0 0 15px rgba(0,0,0,0.3)';
      case 'purplePassion':
        return '0 0 20px rgba(0,0,0,0.5)';
      default:
        return '0 0 15px rgba(0,0,0,0.12)';
    }
  };

  const getRGBA = (color: string | undefined, alphaPercent: number): string => {
    const alpha = Math.min(Math.max(alphaPercent, 0), 100) / 100;

    if (!color || color === 'transparent') return 'rgba(0,0,0,0)';

    if (color.startsWith('rgba')) {
      return color;
    }

    if (color.startsWith('rgb')) {
      // Add alpha to existing rgb()
      const [r, g, b] = color.match(/\d+/g)!.map(Number).slice(0, 3);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    // Handle hex like #293441
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const containerStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: currentContainerProps.flexDirection || 'row',
    alignItems: currentContainerProps.alignItems || 'center',
    justifyContent: currentContainerProps.justifyContent || 'center',
    gap: currentContainerProps.gap ?? 4,
    position: 'relative',
    paddingTop: currentContainerProps.paddingTop ?? 0,
    paddingRight: currentContainerProps.paddingRight ?? 0,
    paddingBottom: currentContainerProps.paddingBottom ?? 0,
    paddingLeft: currentContainerProps.paddingLeft ?? 0,
    backgroundColor: getRGBA(
      currentContainerProps.backgroundColor || currentBackgroundColor,
      currentBackgroundAlpha ?? 100,
    ),
    border:
      isHovered && dashMode === 'Edit'
        ? '1px solid gray'
        : `${currentBorderWidth}px solid ${currentContainerProps.borderColor || currentBorderColor}`,
    cursor: isPresentationMode ? 'pointer' : 'move',
    overflow: 'hidden',
    boxSizing: 'border-box',
    boxShadow: getBoxShadow(),
    transform: `rotate(${currentRotation}deg)`,
    transformOrigin: 'center',
    minHeight: 40,
    minWidth: 40,
    transition: 'all 0.2s ease',
  };

  return (
    <Rnd
      size={{ width: width * gridSize.x, height: height * gridSize.y }}
      disableDragging={isPresentationMode}
      enableResizing={!isPresentationMode}
      position={{ x: x * gridSize.x, y: y * gridSize.y }}
      onDragStop={(e, d) => {
        onChange({ x: Math.round(d.x / gridSize.x), y: Math.round(d.y / gridSize.y) });
        setIsDragging(false);
      }}
      onDragStart={() => setIsDragging(true)}
      onResizeStop={(e, direction, ref, delta, position) => {
        const newWidth = Math.round(parseInt(ref.style.width, 10) / gridSize.x);
        const newHeight = Math.round(parseInt(ref.style.height, 10) / gridSize.y);
        const newX = Math.round(position.x / gridSize.x);
        const newY = Math.round(position.y / gridSize.y);
        onChange({ width: newWidth, height: newHeight, x: newX, y: newY });
      }}
      bounds="parent"
      className="dashboard-button"
      minWidth={gridSize.x}
      minHeight={gridSize.y}
      dragGrid={[gridSize.x, gridSize.y]}
      resizeGrid={[gridSize.x, gridSize.y]}
      style={{
        zIndex: 4,
      }}
    >
      <Tooltip
        title={tooltip?.enabled && !confirmOpen ? tooltipText : ''}
        placement="top"
        arrow
        disableInteractive
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '12px',
            },
          },
        }}
      >
        <Box
          onClick={(e: React.MouseEvent<HTMLDivElement>) => {
            if (!isDragging) {
              onClick(e);
              e.stopPropagation();
            }
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          borderRadius={`${currentContainerProps.borderRadius ?? currentBorderRadius ?? (softUI ? 12 : 3)}px`}
          sx={containerStyle}
        >
          <ButtonRender
            contentOrder={contentOrder}
            icon={icon}
            iconStyle={iconStyle}
            iconScale={iconScale}
            iconColor={iconColor}
            iconProps={iconProps}
            text={text}
            colorScheme={colorScheme}
            textProps={textProps}
            imageUrl={imageUrl}
            imageProps={imageProps}
            isHovered={isHovered}
            hoverEnabled={hoverEnabled}
            height={height}
          />

          {isHovered && dashMode === 'Edit' && (
            <div className="DraggableImage-delete-button-container">
              <button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  setConfirmOpen(true);
                }}
                title="Delete"
              >
                ×
              </button>
            </div>
          )}
          <AcceptRejectDialog
            open={confirmOpen}
            heading="Remove Button"
            messages={[{ text: 'Are you sure you want to remove this Button from the dashboard?' }]}
            acceptText="Remove"
            rejectText="Cancel"
            acceptButtonStyle={acceptBtnStyle}
            rejectButtonStyle={rejectBtnStyle}
            acceptFunction={() => {
              onDelete && onDelete(id);
              setConfirmOpen(false);
            }}
            rejectFunction={() => setConfirmOpen(false)}
            closeFunction={() => setConfirmOpen(false)}
          />
        </Box>
      </Tooltip>
    </Rnd>
  );
};

export default DraggableResizableButton;
